import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { useTranslations } from 'use-intl';
import {
  type Locale,
  supportedLanguages,
} from '@/features/locale/supported-languages';
import type { QuestionInput, QuestionText } from '../actions.schema';

export function emptyQuestionText(): QuestionText {
  return Object.fromEntries(
    supportedLanguages.map((locale) => [locale, '']),
  ) as QuestionText;
}

// Shared field layout for the add/update feedback-question forms. Ports the
// description input plus the per-locale before/after grid from the Next
// `add-question` / `update-question` conform forms into a controlled component.
export function QuestionFields({
  value,
  onChange,
  idPrefix,
}: {
  value: QuestionInput;
  onChange: (next: QuestionInput) => void;
  idPrefix: string;
}) {
  const t = useTranslations('survey');
  const t_lang = useTranslations('languages');

  const setDescription = (description: string) => {
    onChange({ ...value, description });
  };
  const setBefore = (locale: Locale, text: string) => {
    onChange({
      ...value,
      beforeQuestion: { ...value.beforeQuestion, [locale]: text },
    });
  };
  const setAfter = (locale: Locale, text: string) => {
    onChange({
      ...value,
      afterQuestion: { ...value.afterQuestion, [locale]: text },
    });
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-description`}>{t('description')}</Label>
        <Input
          id={`${idPrefix}-description`}
          type="text"
          placeholder={t('description')}
          value={value.description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="text-md grid grid-cols-2 gap-4 font-semibold">
        <div>{t('before_course')}</div>
        <div>{t('after_course')}</div>
      </div>
      {supportedLanguages.map((locale) => (
        <div className="grid grid-cols-2 gap-4" key={locale}>
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${idPrefix}-b-${locale}`}>
              {t('question_in', { locale: t_lang(locale) })}
            </Label>
            <Input
              id={`${idPrefix}-b-${locale}`}
              type="text"
              placeholder={t('question')}
              value={value.beforeQuestion[locale]}
              onChange={(e) => setBefore(locale, e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${idPrefix}-a-${locale}`}>
              {t('question_in', { locale: t_lang(locale) })}
            </Label>
            <Input
              id={`${idPrefix}-a-${locale}`}
              type="text"
              placeholder={t('question')}
              value={value.afterQuestion[locale]}
              onChange={(e) => setAfter(locale, e.target.value)}
            />
          </div>
        </div>
      ))}
    </>
  );
}
